import { faArrowLeft, faCalendarCheck, faFileInvoiceDollar, faGraduationCap, faHome, faLock, faUserCheck, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Button, Paper, Skeleton, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router';
import { BreadCrumbCustom } from '../../../components/BreadCrumbCustom/BreadCrumbCustom';
import { SubList } from '../../../components/SubList/SubList';
import { useLang } from '../../../language';
import { setEmpresaCif } from '../../../store/slices/empresa';
import { RootState } from '../../../store/store';
import { EMPRESA_DETALLE_PATHS } from '../controllers/empresaDetalle.routes';
import { EmpresasServices } from '../controllers/services/empresas.services';
import { CatalogoCursos } from './CatalogoCursos';
import { Facturas } from './Facturas';
import { Inici } from './Inici';
import { MisActividades } from './MisActividades';
import { MisDatos } from './MisDatos';
import { Personas } from './Personas';

export const EmpresaDetalle = ({ onlyPath = '/empreses' }: any) => {

    const Lang = useLang()

    const dispatch = useDispatch()

    const { empresaId } = useParams()

    const navigate = useNavigate()

    const parentPath = `${onlyPath}/${empresaId}`

    const listRoutes = [
        {
            title: Lang('INICIO'),
            link: EMPRESA_DETALLE_PATHS.INICI,
            icon: faHome
        },
        {
            title: Lang('FACTURAS'),
            link: EMPRESA_DETALLE_PATHS.FACTURAS,
            icon: faFileInvoiceDollar
        },
        {
            title: Lang('MIS_ACTIVIDADES'),
            link: EMPRESA_DETALLE_PATHS.MIS_ACTIVIDADES,
            icon: faCalendarCheck
        },
        {
            title: Lang('PERSONAS'),
            link: EMPRESA_DETALLE_PATHS.PERSONAS,
            icon: faUsers
        },
        {
            title: Lang('CATALOGO_DE_CURSOS'),
            link: EMPRESA_DETALLE_PATHS.CATALOGO_CURSOS,
            icon: faGraduationCap
        },
        {
            title: Lang('MIS_DATOS'),
            link: EMPRESA_DETALLE_PATHS.MIS_DATOS,
            icon: faUserCheck
        },
    ]

    const [BreadcrumbLineItems, setBreadcrumbLineItems] = useState<any>([
        {
            title: Lang('EMPRESAS'),
            link: onlyPath
        },
        {
            // Mientras carga la empresa se muestra un loading en lugar de "Detalle empresa"
            title: <Skeleton variant="text" width={180} />,
            link: parentPath
        }
    ])

    // Datos de la empresa (nombre para el breadcrumb, NIF/CIF y resto de campos para
    // el registro de personas en actividades del WS d'Activitats).
    const [fetchEmpresaGET, fetchEmpresaGETData] = EmpresasServices.GET_EMPRESA()

    const empresa = fetchEmpresaGETData.data

    // POST {WS}/integraWeb.php  accion=comprobar-empresa (body JSON)
    const [fetchComprobarPOST] = EmpresasServices.COMPROBAR_EMPRESA()

    // Si falla la carga del detalle (p. ej. 403 cuando el usuario no tiene acceso
    // autorizado a la empresa), se muestra el mensaje que llega en el JSON del
    // servidor (`{ "error": "Accés no autoritzat" }`) en lugar de las pestañas.
    const errorEmpresa = fetchEmpresaGETData.error

    const mensajeErrorEmpresa = errorEmpresa?.error || errorEmpresa?.message || ''

    useEffect(() => {

        if (empresaId) fetchEmpresaGET({ id: Number(empresaId) })

    }, [empresaId])

    // El CIF de la empresa viaja en la cabecera `X-Empresa-Cif` de todas las llamadas
    // al WS d'Activitats (ver `useIntegraWeb` en
    // controllers/services/noticias.services.ts). Como el fetcher se instancia sin
    // argumentos (`NoticiasServices.GET_NOTICIAS()` y compañía), el CIF se pasa por
    // redux en lugar de por prop.
    //
    // Se limpia al desmontar y mientras no hay CIF, para que nunca salga el de la
    // empresa anterior: `useAxios` pone `data` a null al lanzar la petición, así que
    // al cambiar de `empresaId` el CIF se vacía solo hasta que llega el nuevo. Si la
    // carga falla con 403 se queda a '' y no se manda la cabecera.
    useEffect(() => {

        dispatch(setEmpresaCif(empresa?.cif || ''))

        return () => {
            dispatch(setEmpresaCif(''))
        }

    }, [empresa?.cif])

    // El CIF tal y como está en redux ahora mismo. Las pestañas no se montan hasta
    // que coincide con el de la empresa cargada: en un montaje, React dispara los
    // `useEffect` de los hijos ANTES que los del padre, así que aunque el detalle ya
    // esté en memoria, la carga inicial de una pestaña se lanzaría antes de que este
    // componente llegue a escribir el CIF en redux (y saldría sin cabecera). Al
    // esperar aquí a que el store esté al día, las pestañas siempre montan con el
    // CIF ya puesto.
    const cifEnStore = useSelector((state: RootState) => state.empresa.cif)

    const empresaListaParaMontarPestanas = !!empresa && cifEnStore === empresa.cif

    // Al entrar en el detalle de una empresa se informa al WS d'Activitats con la
    // data completa de la empresa para que la compruebe (accion=comprobar-empresa).
    //
    // Solo se envía si el detalle ha cargado: cuando el usuario no tiene acceso
    // autorizado el GET devuelve 403 y `empresa` queda a null, que es
    // exactamente el caso en el que no hay nada que comprobar.
    //
    // Se manda `empresa.raw`, el objeto tal cual devuelve
    // /integra-web/empresas/consultar/{id} (columnas de `integra_clientes`,
    // `persona_fisica`, categorías y departamentos), igual que se hace en
    // `inscribir_personas_actividad`. La respuesta no bloquea la pantalla: el WS
    // va por su cuenta y el error, si lo hubiera, no afecta al detalle.
    useEffect(() => {

        if (!empresaId || !empresa?.raw) return

        fetchComprobarPOST({
            empresa_id: Number(empresaId),
            cif: empresa?.cif,
            empresa: empresa?.raw,
        })

    }, [empresaId, empresa?.cif])

    // Convierte el nombre a formato 'título' (primera letra de cada palabra en
    // mayúscula y el resto en minúscula), manteniendo en mayúscula las siglas
    // clásicas (S.L., S.A., S.C.P...) para que no salga todo el nombre en mayúsculas.
    const nombreTitulo = (nombre: string) => String(nombre || '')
        .toLowerCase()
        .split(/\s+/)
        .map((palabra: string) => {
            if (palabra.includes('.')) return palabra.toUpperCase()
            return palabra ? palabra.charAt(0).toUpperCase() + palabra.slice(1) : palabra
        })
        .join(' ')

    // Sustituye el loading del breadcrumb por el nombre de la empresa, con la
    // primera letra en mayúscula y el color primary de la app.
    useEffect(() => {

        const nombre = empresa?.nombre

        if (!nombre) return

        setBreadcrumbLineItems((item: any) => item.map((i: any, index: number) => index === 1 ? { ...i, title: nombreTitulo(nombre), color: 'primary' } : i))

    }, [empresa?.nombre])

    // Si falla la carga de la empresa, vuelve al texto por defecto
    useEffect(() => {

        if (!fetchEmpresaGETData.error) return

        setBreadcrumbLineItems((item: any) => item.map((i: any, index: number) => index === 1 ? { ...i, title: Lang('DETALLE_EMPRESA'), color: undefined } : i))

    }, [fetchEmpresaGETData.error])

    return (
        <div>
            <BreadCrumbCustom items={BreadcrumbLineItems} />

            <section className='LayoutPrimary__contentmain'>

                {
                    errorEmpresa ?

                        <Paper className='p-3 p-md-4' sx={{ borderRadius: 2.5 }}>

                            <Box sx={{ textAlign: 'center', py: { xs: 4, md: 6 } }}>

                                <FontAwesomeIcon icon={faLock} className='color-primary fa-2x' />

                                <Typography variant='h6' className='my-2 fw-500 color-primary'>
                                    {Lang('SIN_ACCESO_EMPRESA')}
                                </Typography>

                                <Typography variant='body2' className='my-0' sx={{ color: 'text.secondary' }}>
                                    {mensajeErrorEmpresa || Lang('ERROR_CARGAR_EMPRESA')}
                                </Typography>

                                <Box sx={{ mt: 3 }}>
                                    <Button
                                        variant='outlined'
                                        color='primary'
                                        startIcon={<FontAwesomeIcon icon={faArrowLeft} style={{ fontSize: 11 }} />}
                                        onClick={() => navigate(onlyPath)}
                                    >
                                        {Lang('VOLVER')}
                                    </Button>
                                </Box>

                            </Box>

                        </Paper>

                        :

                        <>

                            <Paper className='mb-3' sx={{ borderRadius: 2, p: 0.5 }}>

                                <SubList
                                    listRoutes={listRoutes}
                                    parentPath={parentPath}
                                    horizontal
                                />

                            </Paper>

                            {/* Las pestañas no se montan hasta que la empresa ha cargado Y su
                                CIF ya está en redux. Sus `useEffect` de carga inicial llaman
                                al WS d'Activitats en el propio montaje, y como en un montaje
                                los efectos de los hijos corren antes que los del padre,
                                esperar solo a `empresa` no bastaba: la primera petición
                                salía sin la cabecera `X-Empresa-Cif`. Con este gate, cuando
                                montan el CIF ya está en el store y todas sus peticiones lo
                                llevan.

                                Antes también se montaban con la empresa sin cargar, lo que
                                además gastaba las llamadas del WS en una empresa a la que el
                                usuario puede que ni tenga acceso (el 403 llega después). */}
                            {
                                !empresaListaParaMontarPestanas
                                ?
                                <Skeleton variant="rounded" height={220} />
                                :
                                <Routes>

                                    <Route
                                        path={listRoutes[0].link}
                                        element={<Inici setBreadcrumbLineItems={setBreadcrumbLineItems} route={listRoutes[0]} empresa={empresa} />}
                                    />

                                    <Route
                                        path={listRoutes[1].link}
                                        element={<Facturas setBreadcrumbLineItems={setBreadcrumbLineItems} route={listRoutes[1]} empresa={empresa} />}
                                    />

                                    <Route
                                        path={listRoutes[2].link}
                                        element={<MisActividades setBreadcrumbLineItems={setBreadcrumbLineItems} route={listRoutes[2]} empresa={empresa} />}
                                    />

                                    <Route
                                        path={listRoutes[3].link}
                                        element={<Personas setBreadcrumbLineItems={setBreadcrumbLineItems} route={listRoutes[3]} />}
                                    />

                                    <Route
                                        path={listRoutes[4].link}
                                        element={<CatalogoCursos setBreadcrumbLineItems={setBreadcrumbLineItems} route={listRoutes[4]} />}
                                    />

                                    <Route
                                        path={listRoutes[5].link}
                                        element={<MisDatos setBreadcrumbLineItems={setBreadcrumbLineItems} route={listRoutes[5]} empresa={empresa} />}
                                    />

                                    <Route
                                        path="*"
                                        element={<Navigate to={`${parentPath}${listRoutes[0].link}`} replace />}
                                    />

                            </Routes>
                            }

                        </>
                }

            </section>
        </div>
    )
}
